/**
 * Geometry for the browse map. Public listing pins are deliberately coarse —
 * the API rounds them to suburb precision — so every listing in Borrowdale
 * arrives at exactly the same coordinate and, plotted as-is, would stack into a
 * single pin. `jitterPins` fans a stack out by a little over 100 m, which is
 * well inside the precision the payload already admits to and far enough apart
 * to click. `boundsOf` gives the map its opening frame.
 */

// ~0.0012° of latitude is about 130 m — under the 150 m the spec allows, and
// enough separation that two pins do not overlap at suburb zoom.
const RING_RADIUS = 0.0012;
const PER_RING = 6;

// Number(null) is 0, which would plant a listing with no pin in the Gulf of
// Guinea — so a missing coordinate is rejected before the numeric check.
const coord = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const hasCoords = (p) => !!p && coord(p.lat) !== null && coord(p.lng) !== null;

/**
 * Returns a new array of `{...pin, lat, lng}` with co-located pins spread around
 * concentric rings. Pins at a coordinate nothing else shares are returned with
 * their coordinates untouched, so a precise pin stays precise.
 */
export const jitterPins = (pins) => {
  const usable = (pins || []).filter(hasCoords).map((p) => ({
    ...p, lat: Number(p.lat), lng: Number(p.lng),
  }));
  const seen = new Map();
  return usable.map((p) => {
    const key = `${p.lat},${p.lng}`;
    const k = seen.get(key) || 0;
    seen.set(key, k + 1);
    if (k === 0) return p;
    // Ring 0 holds six positions; later rings tighten the radius so a suburb
    // with dozens of listings still resolves to distinct, nearby points.
    const ring = Math.floor((k - 1) / PER_RING);
    const radius = RING_RADIUS / (ring + 1);
    const angle = (k * 2 * Math.PI) / PER_RING + (ring * Math.PI) / PER_RING;
    return {
      ...p,
      lat: p.lat + radius * Math.cos(angle),
      // Longitude degrees shrink with latitude; at Zimbabwe's ~18°S that is a
      // 5% correction, small enough that the plain offset stays under 150 m.
      lng: p.lng + radius * Math.sin(angle),
    };
  });
};

/** The envelope containing every pin, or null when there is nothing to frame. */
export const boundsOf = (pins) => {
  const usable = (pins || []).filter(hasCoords);
  if (usable.length === 0) return null;
  const lats = usable.map((p) => Number(p.lat));
  const lngs = usable.map((p) => Number(p.lng));
  return {
    south: Math.min(...lats),
    west: Math.min(...lngs),
    north: Math.max(...lats),
    east: Math.max(...lngs),
  };
};
